import {
  createContext,
  use,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * How much vertical breathing room every table in the app gets.
 *
 * "default" is antd's own spacing; "compact" trims the block padding on table
 * cells so rows are shorter. It is a whole-app user preference rather than a
 * per-table one: the app renders ~150 tables, and a reader who dislikes the
 * whitespace on one screen dislikes it on all of them.
 */
export type TableDensity = "default" | "compact";

const STORAGE_KEY = "vivo_table_density";

interface TableDensityContextValue {
  density: TableDensity;
  isCompact: boolean;
  setDensity: (density: TableDensity) => void;
  toggleDensity: () => void;
}

const TableDensityContext = createContext<TableDensityContextValue>({
  density: "default",
  isCompact: false,
  setDensity: () => {},
  toggleDensity: () => {},
});

function readStoredDensity(): TableDensity {
  try {
    return localStorage.getItem(STORAGE_KEY) === "compact"
      ? "compact"
      : "default";
  } catch {
    // Private mode / blocked storage: fall back to the default spacing rather
    // than taking the whole app down over a display preference.
    return "default";
  }
}

function persistDensity(density: TableDensity) {
  try {
    if (density === "default") {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, density);
    }
  } catch {
    // The preference just won't survive a reload; the session still honours it.
  }
}

export function TableDensityProvider({ children }: { children: ReactNode }) {
  const [density, setDensity] = useState<TableDensity>(readStoredDensity);

  const selectDensity = useCallback((next: TableDensity) => {
    persistDensity(next);
    setDensity(next);
  }, []);

  const toggleDensity = useCallback(() => {
    setDensity((current) => {
      const next = current === "compact" ? "default" : "compact";
      persistDensity(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      density,
      isCompact: density === "compact",
      setDensity: selectDensity,
      toggleDensity,
    }),
    [density, selectDensity, toggleDensity],
  );

  return <TableDensityContext value={value}>{children}</TableDensityContext>;
}

// eslint-disable-next-line react-refresh/only-export-components -- context file intentionally exports both provider and hook
export function useTableDensity() {
  return use(TableDensityContext);
}
